import { Header, Footer } from "../components/Layout";
import { Link } from "react-router";
import { Building2, Music, UtensilsCrossed, Sofa, Wine, ArrowRight, ChevronRight, Star, CircleDot, Sparkles, User, MapPinned, Gift, CreditCard, DollarSign, MapPin, Heart, Calendar, Users, Play } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import { useState, useMemo, useCallback } from "react";
import { ImageWithFallback } from "../components/figma/ImageWithFallback";
import { usePageTheme } from "../components/usePageTheme";
import { useHome } from "@/hooks/useHome";
import { useAuth } from "@/context/AuthContext";
import { getVenuePrimaryImageUrl, thumbnailDisplayUrl, thumbnailSrcSet } from "@/lib/venueImages";
import { venuesApi } from "@/lib/api";
import type { Venue } from "@/types/api";
import { SlideUp, FadeIn, ScaleIn, StaggerContainer, StaggerItem } from "../components/animations";

import heroImage from "@/assets/5f303bfb37b835fe0a3fd1437702dde6607e05c2.webp";
import heroVideo from "@/assets/hero.mp4";
import { useSEO, organizationSchema, websiteSchema } from "@/hooks/useSEO";
import { EmptyStateGif } from "../components/EmptyStateGif";

export default function Home() {
  useSEO({
    title: "Listaa – Top Nightclubs, Restaurants & Venues Worldwide",
    description: "Explore top venues — nightclubs, restaurants, lounges & bars worldwide. Listaa is the no-fee marketplace and members community for unforgettable nights.",
    structuredData: [organizationSchema, websiteSchema],
  });
  const { isLight, isGlassy, showDarkEffects, text, textSecondary, textMuted, pillInactive } = usePageTheme();
  const [venueFilter, setVenueFilter] = useState("All Venues");
  const [videoId, setVideoId] = useState<string | null>(null);
  const [isFilterLoading, setIsFilterLoading] = useState(false);
  const [filteredVenues, setFilteredVenues] = useState<Venue[] | null>(null);

  const venueTypeIcons: Record<string, React.ElementType> = {
    Nightclub: Music,
    Restaurant: UtensilsCrossed,
    Lounge: Sofa,
    Bar: Wine,
  };

  // Fetch home data from API
  const { isAuthenticated } = useAuth();
  const { data: homeData, isLoading: isLoadingHome } = useHome(true);

  /** Split hero title for two-line layout: prefer newlines when present, else commas (split('\n')[0] alone would use the whole string when there is no \n). */
  const heroTitleParts = useMemo(() => {
    const title = homeData?.hero?.title?.trim() ?? '';
    const fallbackLine1 = 'No Fees';
    const fallbackLine2 = 'Just Good Times & Rewards';
    if (!title) return { line1: fallbackLine1, line2: fallbackLine2 };
    if (title.includes('\n')) {
      const lines = title.split('\n').map((s) => s.trim()).filter(Boolean);
      return {
        line1: lines[0] ?? fallbackLine1,
        line2: lines.slice(1).join(' ') || fallbackLine2,
      };
    }
    const commaParts = title.split(',').map((s) => s.trim()).filter(Boolean);
    return {
      line1: commaParts[0] ?? fallbackLine1,
      line2: commaParts.slice(1).join(', ') || fallbackLine2,
    };
  }, [homeData?.hero?.title]);

  // Helper function to extract venue type from tags
  const getVenueType = (tags?: { id: number; name: string; type: string; category?: string }[]): string => {
    if (!tags || !Array.isArray(tags) || tags.length === 0) return "Venue";

    // Look for venue type tags (category or type based) - safe navigation
    const typeTag = tags.find(t =>
      t?.name && ["Nightclub", "Restaurant", "Lounge", "Bar", "Club"].includes(t.name)
    );

    return typeTag?.name || tags[0]?.name || "Venue";
  };

  // Helper function to determine price range from settings or default
  const getPriceRange = (_settings: any): string => {
    // if (!settings?.bottle?.minAmount) return "$$$";
    // const minAmount = settings.bottle.minAmount;
    // if (minAmount >= 1000) return "$$$$";
    // if (minAmount >= 500) return "$$$";
    // if (minAmount >= 200) return "$$";
    return "$";
  };

  // Transform API venues to display format
  const featuredVenues = useMemo(() => {
    const sourceVenues = filteredVenues ?? homeData?.featuredVenues;
    if (!sourceVenues || sourceVenues.length === 0) return [];

    return sourceVenues.map(v => ({
      id: v.slug || v.id,
      slug: v.slug,
      name: v.name,
      type: getVenueType(v.tags),
      neighborhood: v.state || (v.address ? v.address.split(",").pop()?.trim() : "") || v.location || "",
      price: getPriceRange(v.settings),
      image: thumbnailDisplayUrl(getVenuePrimaryImageUrl(v), 800),
      description: v.description || "",
      rating: v.rating || 4.5,
      reviews: v.reviewCount || 0,
      featured: true,
    }));
  }, [homeData?.featuredVenues, filteredVenues]);

  // Get categories from home data or use defaults
  const categories = useMemo(() => {
    if (homeData?.categories && homeData.categories.length > 0) {
      return homeData.categories.map(c => ({ label: c }));
    }
    return [
      { label: "All Venues" },
      { label: "Nightclubs" },
      { label: "Restaurants" },
      { label: "Lounges" },
      { label: "Bars" },
    ];
  }, [homeData?.categories]);

  const resolveVenueTagId = useCallback((label: string): number | null => {
    const tags = homeData?.venueTags;
    if (!tags) return null;
    const venueTypeTags = tags.venue_type || tags.venue || tags.Venue || tags["Venue Type"];
    if (!Array.isArray(venueTypeTags)) return null;
    const labelToTag: Record<string, string[]> = {
      "Nightclubs": ["Nightclub", "Club", "Nightclubs"],
      "Restaurants": ["Restaurant", "Restaurants"],
      "Lounges": ["Lounge", "Lounges"],
      "Bars": ["Bar", "Bars"],
    };
    const candidates = labelToTag[label] || [label];
    const match = venueTypeTags.find((t: any) => candidates.some(c => t?.name?.toLowerCase() === c.toLowerCase()));
    return match?.id ?? null;
  }, [homeData?.venueTags]);

  const handleCategoryFilter = useCallback(async (cat: { label: string }) => {
    setVenueFilter(cat.label);

    if (cat.label === "All Venues") {
      setFilteredVenues(null);
      return;
    }

    const tagId = resolveVenueTagId(cat.label);
    if (tagId == null) return;

    setIsFilterLoading(true);
    try {
      const results = await venuesApi.filterVenues({ venue: [tagId] });
      setFilteredVenues(results);
    } catch (err) {
      console.error("Failed to filter venues:", err);
      setFilteredVenues([]);
    } finally {
      setIsFilterLoading(false);
    }
  }, [resolveVenueTagId]);


  const isLoadingVenues = isLoadingHome || isFilterLoading;

  return (
    <div className={`min-h-screen relative transition-colors duration-500 ${isLight ? "bg-gradient-to-b from-rose-50 via-amber-50/80 to-sky-100" : "bg-gradient-to-b from-[#0a0a0f] via-[#1a0a1f] to-[#0a0a0f]"}`}>
      {/* Hero Background Image + Overlay */}
      {showDarkEffects && <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Central warm glow at top */}
        <div className="absolute top-[-200px] left-1/2 -translate-x-1/2 w-[1000px] h-[600px] rounded-full bg-[#ff3366]/[0.15] blur-[150px]"></div>
        {/* Left accent glow */}
        <div className="absolute top-[-100px] left-[5%] w-[500px] h-[400px] rounded-full bg-purple-600/[0.12] blur-[120px]"></div>
        {/* Right accent glow */}
        <div className="absolute top-[-100px] right-[5%] w-[500px] h-[400px] rounded-full bg-[#ff3366]/[0.10] blur-[120px]"></div>
        {/* Mid-page subtle warmth */}
        <div className="absolute top-[30%] left-1/2 -translate-x-1/2 w-[700px] h-[250px] rounded-full bg-purple-900/[0.15] blur-[140px]"></div>

        {/* Star particles */}
        <div className="absolute w-[2px] h-[2px] bg-white/50 rounded-full top-[4%] left-[8%] animate-pulse"></div>
        <div className="absolute w-[1px] h-[1px] bg-white/40 rounded-full top-[7%] left-[18%] animate-pulse delay-100"></div>
        <div className="absolute w-[2px] h-[2px] bg-white/35 rounded-full top-[3%] left-[32%] animate-pulse delay-200"></div>
        <div className="absolute w-[1px] h-[1px] bg-white/45 rounded-full top-[9%] left-[42%] animate-pulse delay-300"></div>
        <div className="absolute w-[2px] h-[2px] bg-white/30 rounded-full top-[5%] left-[55%] animate-pulse delay-150"></div>
        <div className="absolute w-[1px] h-[1px] bg-white/40 rounded-full top-[2%] left-[65%] animate-pulse delay-500"></div>
        <div className="absolute w-[2px] h-[2px] bg-white/35 rounded-full top-[8%] left-[78%] animate-pulse delay-75"></div>
        <div className="absolute w-[1px] h-[1px] bg-white/50 rounded-full top-[6%] left-[88%] animate-pulse delay-200"></div>
        <div className="absolute w-[1px] h-[1px] bg-white/30 rounded-full top-[14%] left-[5%] animate-pulse delay-300"></div>
        <div className="absolute w-[2px] h-[2px] bg-white/40 rounded-full top-[16%] left-[22%] animate-pulse delay-150"></div>
        <div className="absolute w-[1px] h-[1px] bg-white/35 rounded-full top-[12%] left-[38%] animate-pulse delay-500"></div>
        <div className="absolute w-[2px] h-[2px] bg-white/30 rounded-full top-[18%] left-[52%] animate-pulse delay-100"></div>
        <div className="absolute w-[1px] h-[1px] bg-white/40 rounded-full top-[15%] left-[68%] animate-pulse delay-75"></div>
        <div className="absolute w-[2px] h-[2px] bg-white/35 rounded-full top-[11%] left-[82%] animate-pulse delay-200"></div>
        <div className="absolute w-[1px] h-[1px] bg-white/45 rounded-full top-[20%] left-[92%] animate-pulse delay-300"></div>
        <div className="absolute w-[1px] h-[1px] bg-white/25 rounded-full top-[24%] left-[15%] animate-pulse delay-100"></div>
        <div className="absolute w-[2px] h-[2px] bg-white/30 rounded-full top-[28%] left-[35%] animate-pulse delay-500"></div>
        <div className="absolute w-[1px] h-[1px] bg-white/35 rounded-full top-[22%] left-[58%] animate-pulse delay-150"></div>
        <div className="absolute w-[2px] h-[2px] bg-white/25 rounded-full top-[30%] left-[75%] animate-pulse delay-200"></div>
        <div className="absolute w-[1px] h-[1px] bg-white/30 rounded-full top-[26%] left-[90%] animate-pulse delay-300"></div>
        <div className="absolute w-[1px] h-[1px] bg-white/25 rounded-full top-[35%] left-[10%] animate-pulse delay-150"></div>
        <div className="absolute w-[2px] h-[2px] bg-white/30 rounded-full top-[38%] left-[45%] animate-pulse delay-500"></div>
        <div className="absolute w-[1px] h-[1px] bg-white/20 rounded-full top-[42%] left-[62%] animate-pulse delay-100"></div>
        <div className="absolute w-[2px] h-[2px] bg-white/25 rounded-full top-[45%] left-[28%] animate-pulse delay-200"></div>
        <div className="absolute w-[1px] h-[1px] bg-white/20 rounded-full top-[50%] left-[80%] animate-pulse delay-300"></div>
        <div className="absolute w-[2px] h-[2px] bg-white/25 rounded-full top-[55%] left-[48%] animate-pulse delay-150"></div>
        <div className="absolute w-[1px] h-[1px] bg-white/20 rounded-full top-[60%] left-[18%] animate-pulse delay-500"></div>
        <div className="absolute w-[1px] h-[1px] bg-white/20 rounded-full top-[65%] left-[70%] animate-pulse delay-100"></div>
        <div className="absolute w-[2px] h-[2px] bg-white/15 rounded-full top-[70%] left-[40%] animate-pulse delay-200"></div>
        <div className="absolute w-[1px] h-[1px] bg-white/15 rounded-full top-[75%] left-[85%] animate-pulse delay-300"></div>

        {/* Background image - Glassy theme only */}
        {isGlassy && (
          <>
            <ImageWithFallback
              src={heroImage}
              alt="Nightlife atmosphere"
              className="w-full h-[110vh] object-cover opacity-25 mt-[60vh]"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0a0a0f]/20 to-[#0a0a0f]/80" />
          </>
        )}
        {/* Decorative stars */}
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-0.5 h-0.5 bg-white/20 rounded-full animate-pulse"
            style={{
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animationDelay: `${i * 0.3}s`,
            }}
          />
        ))}
      </div>}

      <Header />
      <main>

      {/* Hero Section */}
      <section className="relative pt-36 sm:pt-44 pb-10 sm:pb-14 px-4 sm:px-6 overflow-hidden min-h-[60vh] lg:min-h-[50vh]">
        {/* Video Background */}
        <div className="absolute inset-0 z-0 top-[72px] sm:top-[80px]">
          <video
            autoPlay
            loop
            muted
            playsInline
            preload="none"
            poster="/hero-poster.webp"
            aria-hidden="true"
            className="w-full h-full object-cover opacity-50"
            ref={(el) => {
              if (!el || el.dataset.srcLoaded) return;
              el.dataset.srcLoaded = "1";
              const src = homeData?.hero?.videoUrl || heroVideo;
              const source = document.createElement("source");
              source.src = src;
              source.type = "video/mp4";
              el.appendChild(source);
              el.load();
            }}
          />
          <div className={`absolute inset-0 bg-gradient-to-b ${isLight ? "from-white/40 via-white/50 to-white/80" : "from-[#0a0a0f]/15 via-[#0a0a0f]/25 to-[#0a0a0f]/95"}`} />
        </div>

        <div className="max-w-5xl mx-auto text-center relative z-10">
          {/* Heading */}
          <SlideUp threshold={0.05}>
          <h1 className={`text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-5 leading-[1.1] tracking-tight ${text}`}>
            {heroTitleParts.line1},
            <br />
            <span className="relative inline-block">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-pink-400 to-[#ff3366]">
                {heroTitleParts.line2}
              </span>
              {/* Underline swirl */}
              <svg
                className="absolute -bottom-2 left-0 w-full h-3 sm:h-4"
                viewBox="0 0 300 12"
                fill="none"
                preserveAspectRatio="none"
              >
                <path
                  d="M2 8C50 2 100 2 150 6C200 10 250 4 298 8"
                  stroke="url(#underlineGrad)"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  fill="none"
                />
                <defs>
                  <linearGradient id="underlineGrad" x1="0" y1="0" x2="300" y2="0" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#a855f7" />
                    <stop offset="0.5" stopColor="#ec4899" />
                    <stop offset="1" stopColor="#f97316" />
                  </linearGradient>
                </defs>
              </svg>
            </span>
          </h1>
          </SlideUp>

          <SlideUp delay={0.15} threshold={0.05}>
          <p className={`text-base sm:text-lg md:text-xl mb-8 max-w-2xl mx-auto leading-relaxed ${textSecondary}`}>
            {homeData?.hero?.subtitle || "From exclusive nightclubs to fine dining restaurants, find and book the perfect venue for your next night out."}
          </p>
          </SlideUp>


          {/* CTA Buttons */}


        </div>
      </section>

      {/* What is Listaa — plain-language purpose statement (also required by Google OAuth
          branding verification: the home page must explain the app without a login). */}
      {/* <section aria-labelledby="about-listaa" className="px-4 sm:px-6 pt-4 pb-8 relative z-10">
        <div className={`max-w-4xl mx-auto rounded-2xl p-6 sm:p-8 ${isLight ? "bg-white/70 border border-gray-200/60" : "bg-white/[0.03] border border-white/[0.06]"}`}>
          <h2 id="about-listaa" className={`text-2xl sm:text-3xl font-bold mb-3 ${text}`}>What is Listaa?</h2>
          <p className={`mb-3 leading-relaxed ${textSecondary}`}>
            Listaa is a free marketplace and members community for nightlife and dining. Anyone can browse
            nightclubs, restaurants, lounges, bars, events, and nightlife talent (DJs, promoters, photographers)
            worldwide without an account. Members can book tables and bottle service, buy event tickets,
            save favorites, chat with friends, and invite friends — with no booking fees.
          </p>
          <p className={`mb-3 leading-relaxed ${textSecondary}`}>
            Venue owners use Listaa to list their venue for free and manage reservations, events, tickets, and guest lists.
            You can create an account with your email or with "Sign in with Google"; Google sign-in is used only to
            create and log in to your account, and the optional Gmail contact import is used only to let you invite friends you choose.
          </p>
          <p className={`text-sm ${textMuted}`}>
            Read our <Link to="/privacy" className="underline">Privacy Policy</Link> and{" "}
            <Link to="/terms" className="underline">Terms of Service</Link>.
          </p>
        </div>
      </section> */}

      {/* Featured Venues Section */}
      <section className="px-4 sm:px-6 py-16 sm:py-20 relative overflow-hidden">
        <div className="max-w-7xl mx-auto relative z-10">
          {/* Header */}
          <div className="flex justify-center mb-10">
            <button
              type="button"
              aria-label="Watch Listaa video"
              onClick={() => setVideoId(window.matchMedia("(min-width: 768px)").matches ? "K1rMjYMVRJY" : "5yjfUbPqzLo")}
              className="group relative flex items-center justify-center w-24 h-16 rounded-2xl bg-gradient-to-r from-[#ff0050] to-[#f97316] shadow-[0_0_40px_rgba(255,0,80,0.45)] transition-transform hover:scale-110"
            >
              <span className="absolute inset-0 rounded-2xl bg-[#ff0050] opacity-40 animate-ping" />
              <Play className="relative w-8 h-8 text-white fill-white" />
            </button>
          </div>
          <Dialog open={!!videoId} onOpenChange={(o) => !o && setVideoId(null)}>
            <DialogContent className="p-0 overflow-hidden border-0 bg-black w-[min(92vw,1100px)] max-w-none sm:max-w-none">
              <DialogTitle className="sr-only">Listaa video</DialogTitle>
              {videoId && (
                <iframe
                  className={`w-full ${videoId === "K1rMjYMVRJY" ? "aspect-video" : "aspect-[9/16] max-h-[85vh]"}`}
                  src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&playsinline=1&rel=0`}
                  title="Listaa video"
                  allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                  allowFullScreen
                />
              )}
            </DialogContent>
          </Dialog>
          <SlideUp>
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
            <div>
              <h2 className={`text-3xl sm:text-4xl md:text-5xl font-bold mb-2 ${text}`}>
                Popular{" "}
                <span className="relative inline-block">
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-pink-400 to-[#ff3366] italic">
                    Places
                  </span>
                  <svg
                    className="absolute -bottom-1 left-0 w-full h-2.5"
                    viewBox="0 0 200 10"
                    fill="none"
                    preserveAspectRatio="none"
                  >
                    <path
                      d="M2 7C40 2 80 2 100 5C120 8 160 3 198 7"
                      stroke="url(#placesUnderline)"
                      strokeWidth="2"
                      strokeLinecap="round"
                      fill="none"
                    />
                    <defs>
                      <linearGradient
                        id="placesUnderline"
                        x1="0"
                        y1="0"
                        x2="200"
                        y2="0"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop stopColor="#a855f7" />
                        <stop offset="1" stopColor="#f97316" />
                      </linearGradient>
                    </defs>
                  </svg>
                </span>
              </h2>
              <p className={`text-sm sm:text-base max-w-lg ${textSecondary}`}>
                Explore popular venues around you,
                <br />
                Book online and share with your friends.
              </p>
            </div>
            <Link
              to="/venues"
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full border text-sm font-medium transition-colors whitespace-nowrap ${isLight ? "border-gray-300 text-gray-700 hover:bg-gray-100" : "border-white/[0.1] text-white hover:bg-white/[0.05]"}`}
            >
              View All Venues
              <ArrowRight size={14} />
            </Link>
          </div>
          </SlideUp>

          {/* Category Filter Tabs */}
          <FadeIn delay={0.1}>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-8">
            {categories.map(
              (tab) => {
                const tabIcons: Record<string, React.ElementType> = {
                  "All Venues": Building2,
                  Nightclubs: Music,
                  Nightclub: Music,
                  Restaurants: UtensilsCrossed,
                  Restaurant: UtensilsCrossed,
                  Lounges: Sofa,
                  Lounge: Sofa,
                  Bars: Wine,
                  Bar: Wine,
                  Club: Music,
                  Clubs: Music,
                };
                const TabIcon = tabIcons[tab.label] || Building2;
                return (
                  <button
                    key={tab.label}
                    onClick={() => handleCategoryFilter(tab)}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs sm:text-sm font-medium transition-all duration-300 ${
                      venueFilter === tab.label
                        ? "bg-gradient-to-r from-[#ff3366] to-orange-500 text-white shadow-lg shadow-[#ff3366]/20"
                        : pillInactive
                    }`}
                  >
                    <TabIcon size={14} />
                    {tab.label}
                    {venueFilter === tab.label && tab.label === "All Venues" && (
                      <span className="bg-white/20 text-white text-[10px] px-1.5 py-0.5 rounded-full ml-0.5">
                        {featuredVenues.length}
                      </span>
                    )}
                  </button>
                );
              }
            )}
          </div>
          </FadeIn>

          {/* Venue Cards Grid */}
          <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6" staggerDelay={0.08}>
            {isLoadingVenues ? (
              // Loading skeleton
              [...Array(6)].map((_, i) => (
                <div
                  key={i}
                  className={`rounded-2xl overflow-hidden ${isLight ? "bg-white/70 border border-gray-200/60" : "bg-white/[0.03] border border-white/[0.06]"}`}
                >
                  <div className="flex items-center justify-between px-4 pt-4 pb-2">
                    <div className={`h-4 w-20 rounded ${isLight ? "bg-gray-200" : "bg-white/10"} animate-pulse`} />
                    <div className={`h-5 w-16 rounded-full ${isLight ? "bg-gray-200" : "bg-white/10"} animate-pulse`} />
                  </div>
                  <div className="mx-3 aspect-[16/10] rounded-xl overflow-hidden">
                    <div className={`w-full h-full ${isLight ? "bg-gray-200" : "bg-white/10"} animate-pulse`} />
                  </div>
                  <div className="p-4 pt-3 space-y-2">
                    <div className={`h-5 w-3/4 rounded ${isLight ? "bg-gray-200" : "bg-white/10"} animate-pulse`} />
                    <div className={`h-4 w-full rounded ${isLight ? "bg-gray-200" : "bg-white/10"} animate-pulse`} />
                    <div className={`h-4 w-1/2 rounded ${isLight ? "bg-gray-200" : "bg-white/10"} animate-pulse`} />
                  </div>
                </div>
              ))
            ) : featuredVenues.length === 0 ? (
              // Empty state
              <div className="col-span-full flex flex-col items-center justify-center py-16">
                <EmptyStateGif art="search" size={130} light={isLight} className="mb-4" />
                <p className={`text-lg font-medium mb-1 ${text}`}>No venues found</p>
                <p className={`text-sm ${textMuted}`}>Try selecting a different category</p>
                <button
                  onClick={() => handleCategoryFilter(categories[0] || { label: "All Venues", tagIds: [] })}
                  className="mt-4 px-6 py-2 rounded-full bg-gradient-to-r from-[#ff3366] to-orange-500 text-white text-sm font-medium hover:opacity-90 transition-opacity"
                >
                  View All Venues
                </button>
              </div>
            ) : (
              featuredVenues.map((venue) => {
                const TypeIcon = venueTypeIcons[venue.type] || Building2;

                return (
                  <StaggerItem key={venue.id}>
                    <Link
                      to={`/${venue.slug}`}
                      className={`group block rounded-2xl overflow-hidden transition-all duration-300 ${isLight ? "bg-white/70 backdrop-blur-sm border border-gray-200/60 shadow-sm hover:shadow-md hover:border-gray-300/60" : "bg-white/[0.03] backdrop-blur-sm border border-white/[0.06] hover:border-white/[0.12] hover:bg-white/[0.05]"}`}
                    >
                      {/* Card Header - Type & Featured Badge */}
                      <div className="flex items-center justify-between px-4 pt-4 pb-2">
                        <div className={`flex items-center gap-1.5 ${textSecondary}`}>
                          <TypeIcon size={13} />
                          <span className="text-[11px] sm:text-xs font-medium">
                            {venue.type}
                          </span>
                        </div>
                        {venue.featured && (
                          <div className="flex items-center gap-1 bg-gradient-to-r from-[#ff3366] to-orange-500 text-white text-[10px] sm:text-[11px] font-semibold px-2.5 py-1 rounded-full">
                            <Star size={10} fill="white" />
                            Featured
                          </div>
                        )}
                      </div>

                      {/* Image */}
                      <div className="relative aspect-[16/10] mx-3 rounded-xl overflow-hidden">
                        <img
                          src={venue.image}
                          srcSet={thumbnailSrcSet(venue.image)}
                          sizes="(max-width: 640px) 100vw, 450px"
                          alt={venue.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                        {/* Overlay badges */}
                        <div className="absolute bottom-2.5 left-2.5 right-2.5 flex items-center justify-between">
                          <div className="flex items-center gap-1 bg-black/50 backdrop-blur-sm rounded-full px-2.5 py-1">
                            <CircleDot size={10} className="text-gray-400" />
                            <span className="ml-1 text-white text-[10px] sm:text-[11px] font-medium">
                              {venue.neighborhood}
                            </span>
                          </div>
                          {/* <div className="bg-black/50 backdrop-blur-sm rounded-full px-2.5 py-1">
                            <span className="text-white text-[10px] sm:text-[11px] font-semibold">
                              {venue.price}
                            </span>
                          </div> */}
                        </div>
                      </div>

                      {/* Card Body */}
                      <div className="p-4 pt-3">
                        <h3 className={`font-semibold text-base sm:text-lg mb-1.5 ${text}`}>
                          {venue.name}
                        </h3>
                        <p className={`text-xs sm:text-[13px] leading-relaxed mb-3 line-clamp-2 ${textMuted}`}>
                          {venue.description}
                        </p>

                        {/* Rating */}
                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-1 bg-[#ff3366]/10 rounded-full px-2 py-0.5">
                            <Star
                              size={11}
                              className="text-[#ff3366]"
                              fill="#ff3366"
                            />
                            <span className="text-[#ff3366] text-xs font-bold">
                              {venue.rating}
                            </span>
                          </div>
                          <span className="text-gray-600 text-xs">
                            {venue.reviews.toLocaleString()} reviews
                          </span>
                        </div>
                      </div>
                    </Link>
                  </StaggerItem>
                );
              })
            )}
          </StaggerContainer>

          {/* Bottom CTA */}
          <ScaleIn>
            <div className="flex justify-center mt-12">
              <Link
                to="/venues"
                className="flex items-center gap-2 px-8 py-3.5 rounded-full bg-gradient-to-r from-[#ff3366] to-orange-500 text-white font-semibold text-sm hover:opacity-90 transition-opacity shadow-lg shadow-[#ff3366]/20"
              >
                Explore All Venues
                <ArrowRight size={16} />
              </Link>
            </div>
          </ScaleIn>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-4 sm:px-6 py-16 sm:py-24 relative overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 items-center">
            {/* Left Side */}
            <div className="lg:pl-16">
              {/* Badge */}
              <FadeIn>
                <div className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-500/15 to-orange-500/5 border border-orange-500/20 rounded-full px-4 py-1.5 mb-6">
                  <Sparkles size={14} className="text-orange-400" />
                  <span className="text-orange-300 text-xs font-medium">
                    Join {homeData?.stats?.members?.toLocaleString() || '10,000'}+ Members
                  </span>
                </div>
              </FadeIn>

              {/* Heading */}
              <SlideUp delay={0.1}>
                <h2 className={`text-4xl sm:text-5xl md:text-6xl font-bold mb-2 leading-tight ${text}`}>
                  Become a
                </h2>
                <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6 leading-tight">
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#ff3366] to-orange-500">
                    Member
                  </span>
                </h2>

                <p className={`text-sm sm:text-base max-w-md mb-10 leading-relaxed ${textSecondary}`}>
                  Unlock exclusive benefits, connect with the best venues, and
                  start earning rewards today.
                </p>
              </SlideUp>

              {/* Feature List */}
              <StaggerContainer className="flex flex-col gap-3 mb-10" staggerDelay={0.08}>
                {[
                  {
                    icon: User,
                    iconBg: "bg-orange-500/15",
                    iconColor: "text-orange-400",
                    title: "Personal Profile",
                    desc: "Create a profile based on your taste and preferences",
                  },
                  {
                    icon: MapPinned,
                    iconBg: "bg-rose-500/15",
                    iconColor: "text-rose-400",
                    title: "Smart Matching",
                    desc: "Find matching venues, events & members around you",
                  },
                  {
                    icon: Gift,
                    iconBg: "bg-amber-500/15",
                    iconColor: "text-amber-400",
                    title: "Exclusive Deals",
                    desc: "Receive deal offers from venues you love",
                  },
                  {
                    icon: CreditCard,
                    iconBg: "bg-sky-500/15",
                    iconColor: "text-sky-400",
                    title: "Direct Payments",
                    desc: "Connect with venues and pay directly (no fees)",
                  },
                  {
                    icon: DollarSign,
                    iconBg: "bg-pink-500/15",
                    iconColor: "text-pink-400",
                    title: "Earn Rewards",
                    desc: "Make money when referring booking leads to venues",
                  },
                ].map((feature) => (
                  <StaggerItem key={feature.title}>
                  <div
                    className={`group flex items-center gap-4 rounded-xl px-4 py-3.5 transition-all duration-300 cursor-pointer ${isLight ? "bg-white/70 border border-gray-200/60 hover:bg-white/90 hover:border-gray-300/60 shadow-sm" : "bg-white/[0.03] border border-white/[0.06] hover:bg-white/[0.05] hover:border-white/[0.1]"}`}
                  >
                    <div
                      className={`w-10 h-10 rounded-xl ${feature.iconBg} flex items-center justify-center flex-shrink-0`}
                    >
                      <feature.icon
                        size={18}
                        className={feature.iconColor}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className={`text-sm font-semibold ${text}`}>
                        {feature.title}
                      </h3>
                      <p className={`text-xs ${textMuted}`}>
                        {feature.desc}
                      </p>
                    </div>
                    <ChevronRight
                      size={16}
                      className="text-gray-600 group-hover:text-gray-400 transition-colors flex-shrink-0"
                    />
                  </div>
                  </StaggerItem>
                ))}
              </StaggerContainer>

              {/* Bottom CTA */}
              <div className="flex flex-wrap items-center gap-5">
                {!isAuthenticated && (
                <Link
                  to="/sign-up"
                  className="flex items-center gap-2 px-7 py-3.5 rounded-full bg-gradient-to-r from-[#ff3366] to-orange-500 text-white font-semibold text-sm hover:opacity-90 transition-opacity shadow-lg shadow-[#ff3366]/20"
                >
                  Join Now, It's Free
                  <ArrowRight size={16} />
                </Link>
                )}
                <div className="flex items-center gap-3">
                  {/* Stacked avatars */}
                  <div className="flex -space-x-2.5">
                    {[0, 1, 2].map((i) => (
                      <div
                        key={i}
                        className="w-8 h-8 rounded-full bg-gradient-to-br from-orange-400 to-pink-500 border-2 border-[#0a0a0f] flex items-center justify-center"
                      >
                        <User size={12} className="text-white" />
                      </div>
                    ))}
                  </div>
                  <div>
                    <p className={`text-sm font-bold leading-none ${text}`}>
                      {homeData?.stats?.members?.toLocaleString() || '10,000'}+
                    </p>
                    <p className={`text-[11px] ${textMuted}`}>
                      Happy Members
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side - Member Card */}
            <ScaleIn delay={0.2}>
              <div className="relative flex justify-center lg:justify-start">
                {/* Floating gift icon */}
                <div className={`absolute -top-3 right-8 lg:right-12 z-20 w-14 h-14 rounded-2xl border flex items-center justify-center shadow-lg rotate-12 ${isLight ? "bg-white border-orange-300/50 shadow-orange-300/20" : "bg-[#1a1a24] border-orange-500/30 shadow-orange-500/10"}`}>
                  <Gift size={24} className={isLight ? "text-orange-500" : "text-orange-400"} />
                </div>

                {/* iOS & Android app mockups */}
                <div className="relative w-[340px] sm:w-[480px] mx-auto">
                  <div className="absolute -inset-8 bg-gradient-to-br from-pink-600/20 via-purple-600/15 to-orange-500/10 rounded-[60px] blur-3xl pointer-events-none" />
                  <img src="/images/app-mockups.webp" alt="Listaa app on iOS and Android" width={1145} height={1136} className="relative w-full h-auto" loading="lazy" />
                  <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 z-30">
                    <div className="flex items-center gap-1.5 bg-[#ff3366] text-white text-xs font-semibold px-4 py-1.5 rounded-full shadow-lg shadow-[#ff3366]/30 whitespace-nowrap">
                      <Star size={13} fill="white" />
                      {/* Available on iOS & Android */}
                      Your Social Life all in 1 App.
                    </div>
                  </div>
                </div>
              </div>
            </ScaleIn>
          </div>
        </div>
      </section>

      </main>
      <Footer />
    </div>
  );
}
